import LogManager from "./logManager";

/**
 * parseLambdaPayload - Decode a RouterLambda response envelope.
 *
 * RouterLambda returns the raw lambda.invoke() result, so the backend envelope is usually a JSON
 * string within `Payload` (needs a second parse), but a singly-encoded object must not throw. This
 * also guards the HTTP status and payload shape so a non-2xx / error-shaped response surfaces the
 * real failure instead of an opaque JSON.parse error ("Unexpected token u"). Shared by every caller
 * that hits RouterLambda so the envelope-decoding contract lives in one place.
 *
 * @param {Response} response - the fetch Response
 * @param {string} label - endpoint label used in error messages (e.g. "/action-items")
 * @returns {Promise<object>} the decoded payload object
 */
export const parseLambdaPayload = async (response, label) => {
  if (!response.ok) {
    throw new Error(`${label} failed with status ${response.status}`);
  }
  const responseJson = await response.json();
  if (!responseJson?.Payload) {
    throw new Error(`Missing Payload in ${label} response`);
  }
  const parsed = JSON.parse(responseJson.Payload);
  return typeof parsed === "string" ? JSON.parse(parsed) : parsed;
};

/**
 * callRouterLambda - POST a JSON body to a RouterLambda endpoint and return the decoded payload.
 *
 * Wraps the log → fetch → parseLambdaPayload sequence that every RouterLambda caller shares. Use
 * this for the common case; callers that must branch on the raw Response before parsing (e.g. a 504
 * timeout, attaching error.status, reading response.text() on non-ok) should keep calling fetch and
 * parseLambdaPayload directly instead.
 *
 * @param {string} endpoint - the RouterLambda path (e.g. "/feature-flags"), also used as the label
 * @param {object} body - the request body, JSON-stringified before sending
 * @returns {Promise<object>} the decoded payload object
 */
export const callRouterLambda = async (endpoint, body) => {
  LogManager.info(`Calling ${endpoint}. Request body: ${JSON.stringify(body)}`);
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseLambdaPayload(response, endpoint);
};
